/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Button } from 'antd';
import { modalMethod } from 'components';
import { FlexBoxCenter } from 'styles';
import {
  useGetSalesSettlementDetail,
  useGetSettlementEvidenceHistory,
  useCreateSettlementTaxInvoice,
  useSaveCertificateIssuance,
  useCheckCertificateIssuance
} from 'hooks';
import arrowLeftGrey from 'images/arrow/arrow_left_grey.png';

import SalesSettlementTabs from './SalesSettlementTabs';
import { settlementAmountColumns, settlementBizColumns, certificateStatusMaps, PopoverHeaderCell } from './schema';
import { Wrapper, FormWrapper, Chip } from './styles';

const customerMap = {
  '0000': `[등록성공]\n공동인증서 등록이 완료 되었습니다.`,
  '0001': `[등록실패]\n다른 사업자등록번호로 발급된 인증서입니다.\n동일한 사업자등록번호의 인증서를 등록해주세요.`,
  '0002': `[등록실패]\n세금계산서를 발행할 수 없는 인증서입니다.\n사업자용 세금계산서 인증서를 등록해주세요.`,
  '0003': `[등록실패]\n세금계산서를 발행할 수 없는 인증서입니다.\n사업자용 세금계산서 인증서를 등록해주세요.`,
  '9999': `[등록실패]\n인증서를 등록하지 못하였습니다.\n다시 시도해주세요.`
};
const popoverContentMap = {
  pointDirectAmount: `이용회사와 제휴점간 직접정산을 진행하는 경우, 회사 직접정산 금액은 총 정산금액에서 제외됩니다.`, // 회사 직접정산 금액
  evidenceDeadlineDate: `증빙마감일 이전에 세금계산서(증빙)발급을 완료 해야 정해진 지급일자에 정산금 지급이 가능합니다.`, // 증빙 마감일
  payDate: `증빙마감일을 초과하여 세금계산서(증빙)발급을 완료 한다면, 입금 예정일이 달라 질 수 있습니다.`, // 입금 예정일
  evidenceMerge: `① 상계 : 매출금액에서 수수료를 차감한 정산금액을 기준으로 매출 세금계산서 한장으로 증빙하는 방식입니다.\n② 별도 : 매출금액과 수수료를 별도의 매출/매입 세금계산서 두장으로 증빙하는 방식입니다.`, // 적격증빙 상계 여부
  payCommissionMerge: `① 상계 : 매출금액에서 수수료를 차감한 금액만 정산금으로 하여 지급합니다.\n② 별도 : 매출금액은 지급하고, 수수료는 별도의 식권대장계좌로 입금받는 방식입니다.`, // 이체 상계 여부
  feeCalcTypeName: `① 거래건 별 : 거래건 별로 수수료율을 적용하여 계산하는 방식입니다.\n② 회사 별 : 이용 회사 별로 거래금액의 합에 수수료율을 적용하여 계산하는 방식입니다.\n③ 제휴점 별 : 제휴점의 거래금액의 합에 수수료율을 적용하여 계산하는 방식입니다.`, // 수수료 계산방식
  taxableEvidenceType: `① 공급자 직접 발행 : 계약에 따라 제휴사가 직접 세금계산서를 발행하는 방식\n② 서비스 내 증빙 발행 : 당사가 제공하는 세금계산서 발급 서비스를 통해 제휴사가 세금계산서를 발행하는 방식\n③ 종이세금계산서 : 전자발행 이용이 어려워, 제휴사 자체적으로 세무 처리를 수행해야 하는 경우의 증빙 방식`
};

const SalesSettlementDetail = () => {
  const navigate = useNavigate();
  const { settlementIdx } = useParams();
  const isSubmitting = useRef(false);
  const { mutateAsync: getEvidenceHistory, data: evidenceHistory } = useGetSettlementEvidenceHistory(settlementIdx); // 증빙 내역
  const { mutateAsync: createSettlementTaxInvoice } = useCreateSettlementTaxInvoice(settlementIdx);
  const { mutateAsync: saveCertificateIssuance } = useSaveCertificateIssuance();
  const { mutateAsync: checkCertificateIssuance } = useCheckCertificateIssuance(
    (result) => {
      modalMethod.info(<div style={{ whiteSpace: 'break-spaces' }}>{result?.message}</div>);
    },
    (error) => {
      modalMethod.info(
        <div style={{ whiteSpace: 'break-spaces' }}>
          {error?.response?.data?.message || `인증서를 등록하지 못하였습니다.\n다시 시도해주세요.`}
        </div>
      );
    }
  );

  const { mutateAsync: getSalesSettlementDetail, data } = useGetSalesSettlementDetail(settlementIdx);

  const hasReady = evidenceHistory?.salesEvidences.filter((item) => item.evidenceStatus === 'READY')?.length;
  const isActive =
    data?.taxableEvidenceType !== 'ISSUED_BY_PROVIDER' &&
    hasReady &&
    data?.status === 'CONFIRMED' &&
    (data?.evidenceStatus === 'READY' || data?.evidenceStatus === 'FAILED');

  useEffect(() => {
    getSalesSettlementDetail();
    getEvidenceHistory();
  }, []);

  const checkCertificate = () => {
    setTimeout(async () => {
      const target = await getSalesSettlementDetail();
      checkCertificateIssuance({ bizAccountIdx: target?.bizAccountIdx, partnerContractIdx: target?.partnerContractIdx });
    }, 50);
  };

  const showCertificateIssuance = () => {
    modalMethod.info(
      <div style={{ whiteSpace: 'break-spaces' }}>{`새창에서 공동인증서 등록이 완료된 다음\n[닫기] 버튼을 클릭해주세요`}</div>,
      {
        maskClosable: true,
        okText: '닫기',
        onOk: checkCertificate,
        onCancel: checkCertificate
      }
    );
  };

  const handleSendTaxInvoice = () => {
    if (!isActive) {
      modalMethod.info(
        <div style={{ whiteSpace: 'break-spaces' }}>
          {`지금은 세금계산서 발급 또는 승인을 진행할수 없습니다.\n증빙 발급대기 인 경우 가능합니다.`}
        </div>
      );
      return;
    }

    if (isSubmitting.current) return;

    isSubmitting.current = true;

    let certificateStatus = 'NOT_CERTIFICATE';
    if (data?.taxableEvidenceType === 'PAPER_TAX_INVOICE') {
      certificateStatus = 'PAPER_TAX_INVOICE';
    } else if (!data?.certificateExpiredDate) {
      certificateStatus = 'NOT_CERTIFICATE';
    } else if (data?.certificateExpiredDate && moment().isAfter(data?.certificateExpiredDate)) {
      certificateStatus = 'EXPIRED_CERTIFICATE';
    } else if (data?.certificateExpiredDate && moment().isSameOrBefore(moment(data?.certificateExpiredDate).endOf('date'))) {
      certificateStatus = 'CERTIFICATE';
    }

    modalMethod.confirm(<div style={{ whiteSpace: 'break-spaces' }}>{certificateStatusMaps[certificateStatus].content}</div>, {
      okText: certificateStatusMaps[certificateStatus].okText,
      cancelText: '닫기',
      okButtonProps: { style: { fontWeight: 700, color: '#0067FF' } },
      cancelButtonProps: { style: { color: '#FA503C' } },
      onCancel: () => {
        isSubmitting.current = false;
      },
      onOk: async () => {
        try {
          if (certificateStatus === 'NOT_CERTIFICATE' || certificateStatus === 'EXPIRED_CERTIFICATE') {
            const params = { partnerContractIdx: data?.partnerContractIdx, bizAccountIdx: data?.bizAccountIdx };
            const result = await saveCertificateIssuance(params);

            showCertificateIssuance();
            window.open(result.url, 'sales_certificateModal', 'width=800, height=600');
            return;
          }

          await createSettlementTaxInvoice()
            .then(() => {
              getSalesSettlementDetail();
              modalMethod.info(
                <div style={{ whiteSpace: 'pre-line' }}>
                  {`세금계산서 발급 요청이 완료되었습니다.\n발급 확인 처리까지 약 30분 소요됩니다.\n30분 이후 확인해 주세요.`}
                </div>
              );
            })
            .catch((error) => {
              const isError = error?.response?.data?.status === 400;
              modalMethod.info(
                isError ? (
                  <div style={{ whiteSpace: 'break-spaces' }}>
                    {`현재 증빙 상태에서는 처리할 수 없습니다.\n하단 1:1 문의하기를 이용하여 문의해주세요.`}
                  </div>
                ) : (
                  error?.response?.data?.message
                )
              );
            });
        } finally {
          isSubmitting.current = true;
        }
      }
    });
  };

  const getTaxStatus = () => {
    const isExpired = data?.certificateExpiredDate && moment().isSameOrBefore(moment(data?.certificateExpiredDate).endOf('date'));
    if (data?.taxableEvidenceType === 'ISSUED_BY_PROVIDER' || data?.taxableEvidenceType === 'PAPER_TAX_INVOICE') {
      return '해당 없음';
    }
    if (!isExpired) return <div style={{ color: '#FA503C' }}>등록 필요</div>;
    return data?.certificateExpiredDate || '-';
  };

  return (
    <div style={{ paddingBottom: '60px' }}>
      <FlexBoxCenter justify="space-between" style={{ fontSize: '20px', fontWeight: '700', paddingBottom: '8px' }}>
        <FlexBoxCenter gap="8px" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
          <img src={arrowLeftGrey} alt="" style={{ width: '35px' }} />
          <span>{data?.settlementName}</span>
        </FlexBoxCenter>

        <FlexBoxCenter gap="8px">
          {data?.taxableEvidenceType === 'ELECTRONIC_CERTIFICATE' ? (
            <FlexBoxCenter gap="8px" style={{ fontSize: '14px' }}>
              <div>인증서 만료일</div>
              <Chip label={getTaxStatus()} />
            </FlexBoxCenter>
          ) : null}
          <Button
            type="primary"
            style={{
              height: '35px',
              padding: '0px 16px',
              borderRadius: '4px',
              lineHeight: '24px',
              background: isActive ? '#4BB378' : '#d0d0d0',
              borderColor: isActive ? '#4BB378' : '#d0d0d0'
            }}
            onClick={handleSendTaxInvoice}
          >
            {data?.taxableEvidenceType === 'PAPER_TAX_INVOICE' ? '종이 세금계산서 승인' : '세금계산서 발급요청'}
          </Button>
        </FlexBoxCenter>
      </FlexBoxCenter>

      <Wrapper>
        <div style={{ fontSize: '16px', fontWeight: '700' }}>정산서 개요</div>
        <div style={{ borderRight: '1px solid #f0f0f0' }}>
          <FormWrapper>
            <div className="fieldName">항목</div>
            <div className="fieldName" style={{ width: '100%' }}>
              내용
            </div>
          </FormWrapper>
          {/* 매출 기간 */}
          <FormWrapper>
            <div className="fieldName">매출 기간</div>
            <FlexBoxCenter style={{ width: '100%', padding: '0px 16px', borderTop: '1px solid #f0f0f0' }}>
              {data ? `${data?.settlementStartDate} ~ ${data?.settlementEndDate}` : '-'}
            </FlexBoxCenter>
          </FormWrapper>
          {/* 정산 옵션 */}
          <FormWrapper>
            <div className="fieldName">정산 옵션</div>
            <FlexBoxCenter gap="16px" style={{ width: '100%', padding: '0px 16px', borderTop: '1px solid #f0f0f0' }}>
              <FlexBoxCenter gap="8px">
                <PopoverHeaderCell title="적격증빙 상계 여부" popoverContent={popoverContentMap.evidenceMerge} />
                <Chip label="상계" isActive={data && data?.isEvidenceMerge} />
                <Chip label="별도" isActive={data && !data?.isEvidenceMerge} />
              </FlexBoxCenter>
              <FlexBoxCenter gap="8px">
                <PopoverHeaderCell title="이체 상계 여부" popoverContent={popoverContentMap.payCommissionMerge} />
                <Chip label="상계" isActive={data && data?.isPayCommissionMerge} />
                <Chip label="별도" isActive={data && !data?.isPayCommissionMerge} />
              </FlexBoxCenter>
              <FlexBoxCenter gap="8px">
                <PopoverHeaderCell title="수수료 계산 방식" popoverContent={popoverContentMap.feeCalcTypeName} />
                <Chip label="거래건 별" isActive={data && data?.feeCalcType === 'PER'} />
                <Chip label="회사 별" isActive={data && data?.feeCalcType === 'CLIENT_SUM'} />
                <Chip label="제휴점 별" isActive={data && data?.feeCalcType === 'PARTNER_SUM'} />
              </FlexBoxCenter>
              <FlexBoxCenter gap="8px">
                <PopoverHeaderCell title="매출 증빙 방식" popoverContent={popoverContentMap.taxableEvidenceType} />
                <Chip label="공급자 직접 발행" isActive={data?.taxableEvidenceType === 'ISSUED_BY_PROVIDER'} />
                <Chip label="서비스 내 증빙 발행" isActive={data?.taxableEvidenceType === 'ELECTRONIC_CERTIFICATE'} />
                <Chip label="종이세금계산서" isActive={data?.taxableEvidenceType === 'PAPER_TAX_INVOICE'} />
              </FlexBoxCenter>
            </FlexBoxCenter>
          </FormWrapper>
          {/* 정산 금액 */}
          <FormWrapper>
            <div className="fieldName">정산 금액</div>
            <Table columns={settlementAmountColumns} dataSource={[data]} pagination={false} />
          </FormWrapper>
          {/* 입금 일자 */}
          <FormWrapper>
            <div className="fieldName">입금 일자</div>
            <FlexBoxCenter gap="8px" style={{ padding: '0px 16px' }}>
              <FlexBoxCenter gap="8px">
                <PopoverHeaderCell title="증빙 마감일" popoverContent={popoverContentMap.evidenceDeadlineDate} />
                <Chip label={data?.evidenceDeadlineDate || '-'} />
                <div style={{ color: '#adadad' }}>까지 증빙 완료 시</div>
              </FlexBoxCenter>
              <FlexBoxCenter gap="8px">
                <PopoverHeaderCell title="입금 예정일" popoverContent={popoverContentMap.payDate} />
                <Chip label={data?.payDate || '-'} />
                <div style={{ color: '#adadad' }}>에 입금</div>
              </FlexBoxCenter>
            </FlexBoxCenter>
          </FormWrapper>
          {/* 사업자 정보 */}
          <FormWrapper>
            <div className="fieldName">사업자 정보</div>
            <Table columns={settlementBizColumns} dataSource={[data]} pagination={false} />
          </FormWrapper>
        </div>
      </Wrapper>

      <SalesSettlementTabs salesSettlement={data} />
    </div>
  );
};

export default SalesSettlementDetail;
